/**
 * 2 - Steal a Code Book (03 §2): a walled, ruined village - tight sightlines, doorways,
 * rubble - with the communications post and its radio somewhere inside. Elite garrison,
 * expecting trouble (alert 1 from the start); at alert 3 two elites go to the post.
 */

import type { Point } from '../types';
import {
  checker, clutter, countKind, guardAtDoor, guardsAbout, required, ruleOf, structure, targetsOf,
} from './helpers';
import type { ArchetypeSpec } from './types';

/** Centre to centre: the post's half-diagonal, a drum's 3.5 m cook-off, and room to spare. */
const BARREL_STAND_OFF = 8;

export const codeBook: ArchetypeSpec = {
  id: 'code-book',
  name: 'STEAL A CODE BOOK',
  nativeBiome: 'temperate',
  baseThreat: 4,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 1,
  garrison: 'elite',
  perimeter: 'wall',
  sealed: false,
  layout: 'compound',
  size: { w: [50, 60], d: [30, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const placed: Point[] = [];
    // The post sits in the northern half: the village is between it and the gate.
    const north = { ...g.inner, z1: (g.inner.z0 + g.inner.z1) / 2 };
    const post = required(g, 'commsPost', { target: true, within: north, from: placed, minDist: 0 });
    const houses = 4 + g.rng.int(4);
    for (let i = 0; i < houses; i++) {
      const house = structure(g, 'ruinHouse', { minDist: 8.5, from: placed, margin: 1.2 });
      if (!house && i < 3) g.map.problems.push('code-book: no room for the ruined village');
    }
    const walls = 4 + g.rng.int(4);
    for (let i = 0; i < walls; i++) structure(g, 'ruinWall', { length: Math.round(g.rng.range(3, 7)), margin: 1.3 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);
    // Drums cook off (props.ts), and the post must outlive the fight: none within a blast of it.
    const clearOfPost = [{ x: post.x, z: post.z }];
    for (let i = 0; i < 2; i++) structure(g, 'barrel', { margin: 0.5, minDist: BARREL_STAND_OFF, from: clearOfPost });

    guardAtDoor(g, post);
    guardsAbout(g, 2 + g.rng.int(2));
    return {
      objective: {
        kind: 'useObject', label: 'STEAL CODE BOOK', targetPropIds: [post.id], minFraction: 1,
        rules: [
          { type: 'alertConsequence', level: 3, delaySeconds: 0, effect: 'reinforceTarget' },
          { type: 'recall', mode: 'normal' },
        ],
      },
    };
  },

  asserts(def) {
    const { check, out } = checker('code-book');
    check(
      targetsOf(def, 'commsPost').length === 1 && def.objective.targetPropIds.length === 1,
      'needs exactly one comms post to use',
    );
    check(def.objective.kind === 'useObject', 'objective must be useObject');
    check(def.startAlert >= 1, 'the garrison must start at alert 1');
    check(countKind(def, 'ruinHouse') >= 3, 'the village needs at least 3 ruined houses');
    check(ruleOf(def, 'alertConsequence')?.effect === 'reinforceTarget', 'alert 3 must reinforce the comms post');
    check(def.spawns.some((s) => s.kind === 'elite'), 'the garrison must be elite');
    return out;
  },
};
